import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { SubOrganizations } from "../models/SubOrganization";
import { EmissionGroup } from "../models/EmissionGroup";
import { Assets, isPickableAsset } from "../models/Asset";
import { Auxiliary } from "../models/Activities";
import { UseFormReturn, useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { GetActivities, Activity, SumScopes } from "../models/Activities";
import { OrganizationContext } from "./OrganizationContext";
import FileUtils from "../utils/fileUtils";
import { EvidenceService } from "../service/evidence.service";
import alertService from "../components/alert/alertService";
import emissionFactorService from "../service/emissionFactor.service";
import { EmissionFactorDataV2 } from "../models/EmissionFactors";
import { DROPDOWN_EMISSION_GROUP } from "../components/emission/emission-components";
import { calculateSumScopes } from "../utils/emissionUtils";

export type EvidenceInfo = {
  evidenceFileName: string;
  evidenceS3FileName: string;
};

// Two empty names are the backend's Remove command, so a failed lookup must never fall through to them.
const failEvidence = async (messageKey: string): Promise<never> => {
  await alertService.error(messageKey, "upload-incomplete");
  const error: any = new Error(messageKey);
  error._handled = true;
  throw error;
};

export const getEvidenceFileInfo = async (
  attachmentRef: string,
  attachment: string,
  startDate: number,
  subOrganization: string,
): Promise<EvidenceInfo> => {
  let evidenceFileName = "";
  let evidenceS3FileName = "";

  if (
    (!attachmentRef || attachmentRef === "") &&
    attachment &&
    attachment !== ""
  ) {
    const file = FileUtils.base64ToFile(attachment);

    const presignedUrls = await EvidenceService.createPresignedUrls({
      evidenceFileName: file.name,
      // the upload URL is signed for exactly this many bytes
      evidenceFileSize: file.size,
      suborganizationRef: subOrganization,
      year: startDate,
    });

    if (!presignedUrls) {
      return failEvidence("Evidence-Upload-Failed");
    }

    await EvidenceService.uploadEvidenceToPresignedUrl(
      file,
      presignedUrls.evidenceS3UrlUpload,
    );

    evidenceFileName = presignedUrls.evidenceFileName;
    evidenceS3FileName = presignedUrls.evidenceS3FileName;
  } else if (attachmentRef && attachmentRef !== "") {
    const evidence = await EvidenceService.getAttachmentByRef(attachmentRef);

    if (!evidence) {
      return failEvidence("Evidence-File-Not-Uploaded");
    }

    evidenceFileName = evidence.evidenceFileName;
    evidenceS3FileName = evidence.evidenceS3FileName;
  }

  return { evidenceFileName, evidenceS3FileName };
};

export type InputAmountType = "normal" | "special";

export type FormValuesStepOne = {
  organization: string;
  subOrganizations: string;
  location: string;
  emissionGroup: string;
  assetName: string;
  assetType: string;
  site: string;
};

export type FormValuesStepTwo = {
  startDate: Dayjs;
  endDate: Dayjs | null;
  year: Dayjs;
  monthly: boolean;
  activityGroup?: string;
  activityType: string;
  activityAttribute: string;
  activityEmissionFactorPublisher: string;
  vendor: string;
  unit: string;
  remark: string;
  attachment: string;
  amount: string;
  aux1: string;
  aux2: string;
  aux3: string;
  aux4: string;
  aux5: string;
  inputAmountType: InputAmountType;
  attachmentRef: string;
};

export type ActivityFormPayload = {
  isMonthly: boolean;
  suborganization: number;
  amount: string;
  startDate: string;
  endDate: string | undefined;
  assetRef: string;
  emissionFactorRef: string | null;
  emissionGroup: string;
  year: string;
  evidenceFileName: string;
  evidenceS3FileName: string;
  vendor: string;
  remark: string;
  auxiliary?: Auxiliary;
};

export type TemplateFormPayload = {
  emissionGroup: string;
  isMonthly: boolean;
  suborganization: number;
  amount: string;
  year: string;
  assetRef: string;
  emissionFactorRef: string | null;
  isGenerateMonthly: boolean;
  remark: string;
  auxiliary?: Auxiliary;
  vendor?: string;
};

type AddEmissionState = {
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleOpen: () => void;
  handleClose: (event?: object, reason?: string) => void;
  subOrganizationList: SubOrganizations[];
  emissionGroupList: EmissionGroup[];
  assetList: Assets[];
  // deleted assets too: a saved record still names its asset
  allAssets: Assets[];
  loadDataStep2: (emissionGroup: string) => Promise<void>;
  refreshEmissionFactors: () => Promise<void>;
  stepOneForm: UseFormReturn<FormValuesStepOne, unknown, FormValuesStepOne>;
  stepTwoForm: UseFormReturn<FormValuesStepTwo, unknown, FormValuesStepTwo>;
  emissionFactorsMasterData: EmissionFactorDataV2[];
  activitiesList: Activity[];
  totalActivitiesListNumber: number;
  sumScopes: SumScopes;
  setUpActivitiesListData: (data: GetActivities) => void;
  uploadEvidenceFromCurrentForm: () => Promise<EvidenceInfo>;
  prepareFormDataToCreateOrDraftActivities: (
    evidence: EvidenceInfo,
  ) => ActivityFormPayload;
  emissionMode: EmissionMode;
  setEmissionMode: Dispatch<SetStateAction<EmissionMode>>;
  prepareFormDataToCreateOrDraftTemplates: (
    isGenerateMonthly: boolean,
  ) => TemplateFormPayload;
  year: Dayjs;
  setYear: Dispatch<SetStateAction<Dayjs>>;
  isDisabledAuxInput: boolean;
  disableMonthly: boolean;
  setDisableMonthly: Dispatch<SetStateAction<boolean>>;
  inputAmountType: InputAmountType;
  setInputAmountType: Dispatch<SetStateAction<InputAmountType>>;
};

export enum EmissionMode {
  AddEmission = "AddEmission",
  AddActivityTemplates = "AddActivityTemplates",
}

export const Context = createContext<AddEmissionState>({} as AddEmissionState);

export const AddEmissionProvider = ({ children }: { children: ReactNode }) => {
  const [inputAmountType, setInputAmountType] =
    useState<InputAmountType>("normal");

  const lastFetchParams = useRef<string>("");
  // only the newest request may fill the list or clear the key; two requests can share a key
  const latestFetch = useRef(0);

  const orgContext = useContext(OrganizationContext);

  const getEmissionFactors = async (emissionGroup: string) => {
    const startDateVal = stepTwoForm.getValues("startDate");
    const endDateVal = stepTwoForm.getValues("endDate");

    const currentStartDate =
      startDateVal && dayjs(startDateVal).isValid()
        ? dayjs(startDateVal)
        : dayjs();
    const currentEndDate =
      endDateVal && dayjs(endDateVal).isValid()
        ? dayjs(endDateVal)
        : currentStartDate;

    const safeStartIso = currentStartDate.format("YYYY-MM-DDT00:00:00.000[Z]");

    const safeEndIso = currentEndDate.format("YYYY-MM-DDT23:59:59.999[Z]");

    const paramKey = `${emissionGroup}_${safeStartIso}_${safeEndIso}`;

    if (lastFetchParams.current === paramKey) {
      return null;
    }

    lastFetchParams.current = paramKey;
    const fetchId = ++latestFetch.current;

    let result = null;
    try {
      result = await emissionFactorService.getEmissionFactorsV2({
        group: emissionGroup,
        startActivity: safeStartIso,
        endActivity: safeEndIso,
      });
    } catch {
      // error alert already shown by service
      // a failed list must not block asking again, nor leave the previous period's factors to be saved
      if (fetchId === latestFetch.current) {
        lastFetchParams.current = "";
        result = [];
      }
    }

    // a newer group or period was asked for while this answer was on its way
    if (fetchId !== latestFetch.current) return null;

    return result ?? null;
  };

  const loadDataStep2 = async (emissionGroup: string) => {
    const item = emissionGroupList.find(
      (e) => e.emissionGroup === emissionGroup,
    );

    if (!item) return;

    // จำค่าวันเดิมไว้ก่อน เผื่อกรณีไม่ได้ตั้งใจ Reset วันที่ทิ้ง
    const oldStart = stepTwoForm.getValues("startDate");
    const oldEnd = stepTwoForm.getValues("endDate");

    stepTwoForm.reset();

    // คืนค่าวันที่กลับไป เพื่อให้ API ยิงด้วยวันที่ที่ผู้ใช้เลือกไว้จริงๆ
    if (oldStart) stepTwoForm.setValue("startDate", oldStart);
    if (oldEnd) stepTwoForm.setValue("endDate", oldEnd);

    const result = await getEmissionFactors(item._id);

    if (result != null) {
      setEmissionFactorsMasterData(result);
    }
  };

  const refreshEmissionFactors = async () => {
    const currentGroup = stepOneForm.getValues("emissionGroup");
    if (!currentGroup) return;

    const item = emissionGroupList.find(
      (e) => e.emissionGroup === currentGroup,
    );

    if (!item) return;

    const result = await getEmissionFactors(item._id);

    if (result != null) {
      setEmissionFactorsMasterData(result);
      stepTwoForm.resetField("activityEmissionFactorPublisher");
    }
  };

  // modal
  const [step, setStep] = useState(1);
  const [open, setOpen] = useState(false);

  // step 1
  const stepOneForm = useForm<FormValuesStepOne>({
    mode: "all",
    reValidateMode: "onBlur",
  });

  const subOrganizationList: SubOrganizations[] =
    orgContext?.subOrganizations ?? [];
  const emissionGroupList: EmissionGroup[] = DROPDOWN_EMISSION_GROUP;
  const allAssets: Assets[] = orgContext?.assets ?? [];
  const assetList: Assets[] = allAssets.filter(isPickableAsset);

  // step 2
  const stepTwoForm = useForm<FormValuesStepTwo>({
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: false,
    delayError: 400,
  });

  const [emissionFactorsMasterData, setEmissionFactorsMasterData] = useState<
    EmissionFactorDataV2[]
  >([]);

  const handleOpen = () => {
    lastFetchParams.current = "";
    stepOneForm.reset();
    stepTwoForm.reset();
    setInputAmountType("normal");
    setStep(1);
    setOpen(true);
  };

  const handleClose = (_event?: object, reason?: string) => {
    if (reason !== "backdropClick") {
      setStep(1);
      setOpen(false);
    }
  };

  const getRequiredValues = () => {
    const item = emissionGroupList.find(
      (e) => e.emissionGroup === stepOneForm.getValues("emissionGroup"),
    );

    return {
      emissionGroup: item ? item._id : "",
      suborganization: Number(stepOneForm.getValues("subOrganizations")),
      typeEn: stepTwoForm.getValues("activityType").trim(),
      amount: stepTwoForm.getValues("amount") ?? "0",
      unit: stepTwoForm.getValues("unit").trim(),
      assetRef: stepOneForm.getValues("assetName").trim(),
      attributeEn: stepTwoForm.getValues("activityAttribute").trim(),
      monthly: stepTwoForm.getValues("monthly") ?? false,
    };
  };

  const getOptionalValues = () => {
    const vendor = stepTwoForm.getValues("vendor").trim();
    const auxiliary: Auxiliary = {};
    const aux1 = stepTwoForm.getValues("aux1") ?? "";
    const aux2 = stepTwoForm.getValues("aux2") ?? "";
    const aux3 = stepTwoForm.getValues("aux3") ?? "";
    const aux4 = stepTwoForm.getValues("aux4") ?? "";
    const aux5 = stepTwoForm.getValues("aux5") ?? "";

    if (aux1 !== "") auxiliary.aux1 = aux1;
    if (aux2 !== "") auxiliary.aux2 = aux2;
    if (aux3 !== "") auxiliary.aux3 = aux3;
    if (aux4 !== "") auxiliary.aux4 = aux4;
    if (aux5 !== "") auxiliary.aux5 = aux5;

    return { vendor, auxiliary };
  };

  const getSelectedEmissionFactorRef = (
    attributeEn: string,
    typeEn: string,
    unit: string,
    selectedPublisherVal: string,
  ) => {
    const emissionFactorGroup = emissionFactorsMasterData.find(
      (e) =>
        e.attributeEn === attributeEn && e.typeEn === typeEn && e.unit === unit,
    );

    if (!emissionFactorGroup) return null;

    const selectedPublisher = emissionFactorGroup.publishers.find(
      (p) =>
        p.emissionFactorRefId === selectedPublisherVal ||
        p.publisherRef?.publisher === selectedPublisherVal,
    );

    return selectedPublisher?.emissionFactorRefId || null;
  };

  const uploadEvidenceFromCurrentForm = (): Promise<EvidenceInfo> => {
    const subOrgRefId =
      subOrganizationList.find(
        (s) => s.id === stepOneForm.getValues("subOrganizations"),
      )?.refId ?? "";

    return getEvidenceFileInfo(
      stepTwoForm.getValues("attachmentRef"),
      stepTwoForm.getValues("attachment"),
      stepTwoForm.getValues("startDate").year(),
      subOrgRefId,
    );
  };

  const prepareFormDataToCreateOrDraftActivities = (
    evidence: EvidenceInfo,
  ): ActivityFormPayload => {
    const {
      emissionGroup,
      suborganization,
      typeEn,
      amount,
      unit,
      assetRef,
      attributeEn,
      monthly,
    } = getRequiredValues();
    const { vendor, auxiliary } = getOptionalValues();

    const startDate = stepTwoForm
      .getValues("startDate")
      .format("YYYY-MM-01")
      .trim();
    const endDate =
      stepTwoForm.getValues("endDate") !== null
        ? stepTwoForm.getValues("endDate")?.format("YYYY-MM-01").trim()
        : startDate;

    const remark = stepTwoForm.getValues("remark");
    const emissionFactorRef = getSelectedEmissionFactorRef(
      attributeEn,
      typeEn,
      unit,
      stepTwoForm.getValues("activityEmissionFactorPublisher"),
    );

    const payload: ActivityFormPayload = {
      isMonthly: monthly,
      suborganization,
      amount,
      startDate,
      endDate,
      assetRef,
      emissionFactorRef,
      emissionGroup,
      year: stepTwoForm.getValues("startDate").format("YYYY"),
      evidenceFileName: evidence.evidenceFileName,
      evidenceS3FileName: evidence.evidenceS3FileName,
      vendor,
      remark,
    };

    if (Object.keys(auxiliary).length > 0) payload.auxiliary = auxiliary;

    return payload;
  };

  const prepareFormDataToCreateOrDraftTemplates = (
    isGenerateMonthly: boolean,
  ): TemplateFormPayload => {
    const {
      emissionGroup,
      suborganization,
      typeEn,
      amount,
      unit,
      assetRef,
      attributeEn,
      monthly,
    } = getRequiredValues();
    const { vendor, auxiliary } = getOptionalValues();
    const remark = stepTwoForm.getValues("remark");
    const year = stepTwoForm.getValues("year").format("YYYY");

    const emissionFactorRef = getSelectedEmissionFactorRef(
      attributeEn,
      typeEn,
      unit,
      stepTwoForm.getValues("activityEmissionFactorPublisher"),
    );

    const payload: TemplateFormPayload = {
      emissionGroup,
      isMonthly: monthly,
      suborganization,
      amount,
      year,
      assetRef,
      emissionFactorRef,
      isGenerateMonthly,
      remark,
    };

    if (auxiliary.aux1 !== undefined) payload.auxiliary = auxiliary;
    if (vendor !== "") payload.vendor = vendor;

    return payload;
  };

  const [activitiesList, setActivitiesList] = useState<Activity[]>([]);
  const [totalActivitiesListNumber, setTotalActivitiesListNumber] =
    useState<number>(0);

  const [sumScopes, setSumScopes] = useState<SumScopes>({
    sum: 0,
    s0: 0,
    s1: 0,
    s2: 0,
    s3: 0,
  });

  const setUpActivitiesListData = (data: GetActivities) => {
    setTotalActivitiesListNumber(data.numResult);
    setActivitiesList(data.activities);
    setSumScopes(calculateSumScopes(data.sumScopes));
  };

  const [emissionMode, setEmissionMode] = useState<EmissionMode>(
    EmissionMode.AddEmission,
  );

  const [year, setYear] = useState<Dayjs>(dayjs());
  const [isDisabledAuxInput, setIsDisabledAuxInput] = useState<boolean>(false);
  const [disableMonthly, setDisableMonthly] = useState<boolean>(false);

  useEffect(() => {
    setIsDisabledAuxInput(emissionMode === EmissionMode.AddActivityTemplates);
  }, [emissionMode]);

  return (
    <Context.Provider
      value={{
        step,
        setStep,
        open,
        setOpen,
        handleOpen,
        handleClose,
        subOrganizationList,
        emissionGroupList,
        assetList,
        allAssets,
        loadDataStep2,
        refreshEmissionFactors,
        stepOneForm,
        emissionFactorsMasterData,
        stepTwoForm,
        activitiesList,
        totalActivitiesListNumber,
        sumScopes,
        setUpActivitiesListData,
        uploadEvidenceFromCurrentForm,
        prepareFormDataToCreateOrDraftActivities,
        emissionMode,
        setEmissionMode,
        prepareFormDataToCreateOrDraftTemplates,
        year,
        setYear,
        isDisabledAuxInput,
        disableMonthly,
        setDisableMonthly,
        inputAmountType,
        setInputAmountType,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const AddEmissionContext = () => useContext(Context);
